import { useContext, useState } from "react";
import { Navigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import Loading from "components/atoms/Loading";
import Footer from "components/pageComponents/Footer/Footer";
import { useDeviceType } from "components/hooks";
import { MagnetIcon } from "components/atoms/Icon";
import Button from "components/atoms/buttons/Button";
import { useQuery } from "@tanstack/react-query";
import { BrandSocialCardNew } from "components/molecules/brand/BrandSocialCard";
import { UserContext } from "components/context/UserContext";
import { Helmet } from "react-helmet-async";
import { getBrandPublicProfile } from "../../apis/brand";
import { ROUTES, SUBSCRIBE_TO_NOTIFICATIONS, TOAST_CONFIG } from "../../constants";
import { get, post } from "../../utils/request";
import MagnetInfoCardWithMementos from "./MagnetInfoCardWithMementos";
import NotifyMeEmailInputModal from "./NotifyMeEmailInputModal";

function BrandPublicProfile() {
  const [isEmailInputModalOpen, setIsEmailInputModalOpen] = useState(false);
  const { isUserLoggedIn } = useContext(UserContext);
  const { brandId } = useParams();

  const { isMobile } = useDeviceType();

  const {
    data: brandDetails,
    isLoading,
    refetch,
  } = useQuery<BrandPublic>({
    queryFn: () => get(getBrandPublicProfile(brandId as string)),
    enabled: !!brandId,
    queryKey: ["brandDetails", brandId],
  });

  const handleCreateNoMagnetBrandSubscription = async ({ email }: { email?: string }) => {
    await post(
      SUBSCRIBE_TO_NOTIFICATIONS,
      {
        type: 1,
        id: brandId,
        email,
      },
      {
        processData: () => {
          setIsEmailInputModalOpen(false);
          toast.success("Subscribed to brand", TOAST_CONFIG);
        },
        processError: (errorMessage) => {
          toast.error(
            errorMessage || "Failed to subscribe to brand notification. Please try again after some time.",
            TOAST_CONFIG,
          );
        },
      },
    );
    refetch();
  };

  const openEmailInputModal = () => {
    setIsEmailInputModalOpen(true);
  };

  const closeEmailInputModal = () => {
    setIsEmailInputModalOpen(false);
  };

  // Walkthrough Steps
  // const { walkthroughData, setWalkthroughRun } = useWalkthroughContext();
  // useEffect(() => {
  //   if (walkthroughData.tourActive && brandDetails) {
  //     setWalkthroughRun(true);
  //   }
  // }, [brandDetails, walkthroughData.tourActive]);

  if (!brandId) return <Navigate to={ROUTES.NOT_FOUND} />;

  if (isLoading) return <Loading />;

  if (!brandDetails) return null;

  return (
    <>
      <Helmet>
        <title>{`${brandDetails.name} - MAGNETIQ`}</title>
      </Helmet>
      <div className=" bg-primary text-white">
        {/* Brand Cover Image */}
        <div className="relative  h-[20vh] bg-primary md:h-[40vh]">
          <img
            className="-px-2 absolute  h-[20vh] w-full object-contain md:h-[40vh] md:object-cover"
            src={brandDetails.cover_image_url || ""}
            alt={brandDetails.name}
          />
          <div className="absolute left-0 top-0 z-10 h-full w-full bg-gradient-to-t from-primary" />
        </div>
        <div className="mx-5 min-h-[90vh]  md:mx-10 ">
          {/* Brand Card */}
          {/* eslint-disable-next-line react/jsx-props-no-spreading */}
          <BrandSocialCardNew {...brandDetails} />
          <div className="max-w-3xl font-satoshi text-xs font-medium">
            <p className=" line-clamp-4 opacity-70 hover:line-clamp-none">{brandDetails.about}</p>
          </div>
          <hr className="mt-4 text-white opacity-30 " />
          <div className={` mt-6 px-0  ${brandDetails?.magnets.length && "relative"}`}>
            <div className="mb-3  flex ">
              <MagnetIcon className="-ml-2 h-6 w-6 text-white md:h-9 md:w-9" />
              <p className="text-xs font-bold text-white md:text-xl">Magnets</p>
            </div>
            <div className=" flex flex-col">
              <div className="flex-grow">
                <div className="flex flex-col space-y-7  overflow-scroll scrollbar-hide lg:space-y-2">
                  {brandDetails.magnets.length ? (
                    brandDetails.magnets.map((magnet) => (
                      <MagnetInfoCardWithMementos
                        isMobile={isMobile}
                        key={`${magnet.id}_brand_card_${brandDetails.id}`}
                        brandId={brandDetails.id}
                        magnetId={magnet.id}
                        magnetImage={magnet.image_url}
                        magnetName={magnet.name}
                        magnetType={magnet.type}
                        isInteractionBlocked={!!magnet.is_magnet_interaction_blocked}
                        release={magnet.release}
                        mementos={magnet.mementos}
                        description={magnet.magnetized_because}
                        magnetImageHash={magnet.image_hash}
                      />
                    ))
                  ) : (
                    <div
                      className="col-span-5 flex flex-col items-center justify-center
                 pt-12 2xl:pt-20 "
                    >
                      <h3 className="text-center font-satoshi text-2xl font-bold text-white"> MAGNETS COMING SOON</h3>
                      <p className="text-center font-satoshi  text-white">CLICK BELOW TO GET NOTIFIED</p>
                      <Button
                        /* @ts-ignore */
                        disabled={!!brandDetails.user_has_subscribed}
                        text="NOTIFY ME"
                        onClick={isUserLoggedIn ? handleCreateNoMagnetBrandSubscription : openEmailInputModal}
                        variant="rustPinkSolid"
                        additionalClasses="xs:px-3 !px-4 !py-2  sm:px-6 sm:py-2 mt-4"
                      />

                      {/* @ts-ignore */}
                      {brandDetails.user_has_subscribed && (
                        <p className="my-1 text-xs text-white opacity-80 md:text-sm">
                          You&apos;re already Subscribed! You will be notified once a Magnet is released.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className=" mt-14 ">{!isMobile && <Footer />}</div>
      </div>
      {isEmailInputModalOpen && (
        <NotifyMeEmailInputModal
          open={isEmailInputModalOpen}
          handleSubmitClick={handleCreateNoMagnetBrandSubscription}
          onClose={closeEmailInputModal}
        />
      )}
    </>
  );
}
export default BrandPublicProfile;
